window.AdminDashboard = function AdminDashboard({ setActiveTab, showNotification }) {
  const [metrics, setMetrics] = React.useState({
    total_products: 0,
    published_products: 0,
    total_categories: 0,
    active_jobs: 0,
    closed_jobs: 0,
    draft_jobs: 0,
    total_applications: 0,
    new_applications: 0,
    new_inquiries: 0,
    system_status: 'Operational'
  });
  const [activities, setActivities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchDashboardData();
    const handleSync = () => fetchDashboardData();
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_app_added', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_app_added', handleSync);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await window.apiClient.get('dashboard.php');
      if (res.success) {
        setMetrics(res.metrics || metrics);
        setActivities(res.activities || []);
      }
    } catch (e) {
      console.warn('Dashboard fetch fallback:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      
      {/* Top Banner */}
      <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 bg-emerald-500 rounded-full animate-pulse"></span>
            <span className="font-mono text-xs font-bold text-emerald-700 uppercase tracking-widest">
              SYSTEM STATUS: {metrics.system_status}
            </span>
          </div>
          <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">
            Enterprise Overview Dashboard
          </h1>
          <p className="font-sans text-xs sm:text-sm text-slate-500">
            Real-time telemetry, product inventory, job applications, and website content controls.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <button
            onClick={() => setActiveTab('products')}
            className="flex-grow md:flex-grow-0 bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase px-5 py-3 rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">add</span>
            <span>New Product</span>
          </button>
          <button
            onClick={() => setActiveTab('careers')}
            className="flex-grow md:flex-grow-0 bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono text-xs font-bold uppercase px-5 py-3 rounded-xl border border-slate-300 transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">work</span>
            <span>Post Job Opening</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Card 1: Products */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3 hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between text-slate-500">
            <span className="font-mono text-xs font-bold uppercase">Product Catalogue</span>
            <span className="material-symbols-outlined text-secondary text-2xl">inventory_2</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl font-extrabold text-slate-900">{metrics.total_products}</span>
            <span className="font-mono text-xs text-emerald-600 font-bold">{metrics.published_products} Published</span>
          </div>
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 flex justify-between">
            <span>Categories: {metrics.total_categories}</span>
            <button onClick={() => setActiveTab('products')} className="text-secondary hover:underline font-bold">Manage &rarr;</button>
          </div>
        </div>

        {/* Card 2: Active Job Openings */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3 hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between text-slate-500">
            <span className="font-mono text-xs font-bold uppercase">Active Openings</span>
            <span className="material-symbols-outlined text-secondary text-2xl">work</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl font-extrabold text-slate-900">{metrics.active_jobs}</span>
            <span className="font-mono text-xs text-amber-600 font-bold">{metrics.closed_jobs} Closed</span>
          </div>
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 flex justify-between">
            <span>Drafts: {metrics.draft_jobs}</span>
            <button onClick={() => setActiveTab('careers')} className="text-secondary hover:underline font-bold">Manage &rarr;</button>
          </div>
        </div>

        {/* Card 3: Applications */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3 hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between text-slate-500">
            <span className="font-mono text-xs font-bold uppercase">Career Applications</span>
            <span className="material-symbols-outlined text-secondary text-2xl">badge</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl font-extrabold text-slate-900">{metrics.total_applications}</span>
            {metrics.new_applications > 0 && (
              <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-bold border border-emerald-200 animate-pulse">
                {metrics.new_applications} New
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 flex justify-between">
            <span>Received via portal</span>
            <button onClick={() => setActiveTab('applications')} className="text-secondary hover:underline font-bold">View Applications &rarr;</button>
          </div>
        </div>

        {/* Card 4: Inquiries */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3 hover:border-slate-300 transition-all">
          <div className="flex items-center justify-between text-slate-500">
            <span className="font-mono text-xs font-bold uppercase">New Inquiries & Quotes</span>
            <span className="material-symbols-outlined text-secondary text-2xl">mail</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-4xl font-extrabold text-slate-900">{metrics.new_inquiries}</span>
            <span className="font-mono text-xs text-slate-500 font-bold">Pending Review</span>
          </div>
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 flex justify-between">
            <span>Contact & Quote forms</span>
            <button onClick={() => setActiveTab('inquiries')} className="text-secondary hover:underline font-bold">Inbox &rarr;</button>
          </div>
        </div>

      </div>

      {/* Quick Action Cards & Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Quick Action Cards (5 Cols) */}
        <div className="lg:col-span-5 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <h3 className="font-serif text-xl font-bold text-slate-900">Quick Control Center</h3>
            <span className="font-mono text-[10px] text-slate-400 uppercase font-bold">FAST ACTIONS</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setActiveTab('products')}
              className="bg-slate-50 hover:bg-slate-100 p-4 rounded-xl border border-slate-200 text-left space-y-2 transition-all group cursor-pointer"
            >
              <span className="material-symbols-outlined text-secondary text-2xl group-hover:scale-110 transition-transform">add_box</span>
              <div className="font-bold text-xs text-slate-900">Add New Product</div>
              <div className="text-[10px] text-slate-500">Update catalog items</div>
            </button>

            <button
              onClick={() => setActiveTab('categories')}
              className="bg-slate-50 hover:bg-slate-100 p-4 rounded-xl border border-slate-200 text-left space-y-2 transition-all group cursor-pointer"
            >
              <span className="material-symbols-outlined text-secondary text-2xl group-hover:scale-110 transition-transform">category</span>
              <div className="font-bold text-xs text-slate-900">Manage Categories</div>
              <div className="text-[10px] text-slate-500">Reorder & edit categories</div>
            </button>

            <button
              onClick={() => setActiveTab('careers')}
              className="bg-slate-50 hover:bg-slate-100 p-4 rounded-xl border border-slate-200 text-left space-y-2 transition-all group cursor-pointer"
            >
              <span className="material-symbols-outlined text-secondary text-2xl group-hover:scale-110 transition-transform">post_add</span>
              <div className="font-bold text-xs text-slate-900">Post Job Vacancy</div>
              <div className="text-[10px] text-slate-500">Draft or publish openings</div>
            </button>

            <button
              onClick={() => setActiveTab('media')}
              className="bg-slate-50 hover:bg-slate-100 p-4 rounded-xl border border-slate-200 text-left space-y-2 transition-all group cursor-pointer"
            >
              <span className="material-symbols-outlined text-secondary text-2xl group-hover:scale-110 transition-transform">upload_file</span>
              <div className="font-bold text-xs text-slate-900">Media Manager</div>
              <div className="text-[10px] text-slate-500">Upload images & PDF files</div>
            </button>
          </div>
        </div>

        {/* Activity Timeline (7 Cols) */}
        <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-secondary text-xl">history</span>
              <h3 className="font-serif text-xl font-bold text-slate-900">Audit Activity Timeline</h3>
            </div>
            <button onClick={() => setActiveTab('logs')} className="font-mono text-xs text-secondary hover:underline font-bold">
              View All Logs &rarr;
            </button>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
            {activities.length === 0 ? (
              <div className="text-center text-slate-400 text-xs py-8 font-mono">
                No recent system activity recorded.
              </div>
            ) : (
              activities.map((act, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                  <div className="h-7 w-7 rounded-full bg-slate-200 text-slate-800 flex items-center justify-center font-mono font-bold text-xs shrink-0">
                    {act.username ? act.username.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 truncate">{act.action}</span>
                      <span className="font-mono text-[10px] text-slate-400 shrink-0 ml-2">{act.created_at}</span>
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                      User: <span className="text-slate-800 font-bold">{act.username}</span> | IP: {act.ip_address || '127.0.0.1'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

    </div>
  );
};
